/**
 * The sample song (W2-14 onboarding-wizard; DESIGN.md §5.14 "a short chart on a drum loop synthesised at runtime
 * (OfflineAudioContext; no third-party audio ships)").
 *
 * 16 bars of 4/4 at 120 BPM (32 s) plus a 1.5 s tail, in four sections of four bars: an intro (kick on the
 * downbeat, eighth-note hats), a groove (kick 1 and 3, snare 2 and 4), a lift (open hats, a clap on the snare) and
 * a drive (four-on-the-floor, sixteenth hats, a tom fill at the end), with a crash on every section start and a
 * last hit on beat 64.
 *
 * Each drum voice is synthesised as a one-shot buffer from a seeded noise generator and simple oscillators (pure,
 * deterministic). The loop itself is rendered by an OfflineAudioContext: one buffer source per hit with its gain,
 * hats through a high-pass biquad, everything into a master gain. Where no OfflineAudioContext exists (non-browser
 * hosts) the same hits are mixed here sample by sample. `sampleSongWav` encodes the result as 16-bit WAV (`encodeWav`).
 */

import { encodeWav } from '../../core/audio-dsp/wav';

export const SAMPLE_BPM = 120;
export const SAMPLE_BARS = 16;
export const SAMPLE_BEATS_PER_BAR = 4;
export const SAMPLE_RATE = 44100;
/** Seconds after the last beat (decay of the final hit). */
export const SAMPLE_TAIL_SEC = 1.5;

export type DrumVoice = 'kick' | 'snare' | 'hat' | 'openHat' | 'clap' | 'tom' | 'crash';

export interface DrumHit {
  readonly voice: DrumVoice;
  /** Beat (quarter notes from 0). */
  readonly beat: number;
  /** Linear gain. */
  readonly gain: number;
}

/** Total length of the sample song in seconds. */
export function sampleDurationSec(bars = SAMPLE_BARS, bpm = SAMPLE_BPM): number {
  return (bars * SAMPLE_BEATS_PER_BAR * 60) / bpm + SAMPLE_TAIL_SEC;
}

/** The groove, hit by hit (see the module header). */
export function drumPattern(bars = SAMPLE_BARS): DrumHit[] {
  const hits: DrumHit[] = [];
  const add = (voice: DrumVoice, beat: number, gain: number): void => {
    hits.push({ voice, beat, gain });
  };
  for (let bar = 0; bar < bars; bar++) {
    const b0 = bar * SAMPLE_BEATS_PER_BAR;
    const section = Math.min(3, Math.floor((bar * 4) / Math.max(4, bars)));
    const lastOfSection = bar % 4 === 3;
    if (bar % 4 === 0) add('crash', b0, section === 0 ? 0.35 : 0.5);
    // Kicks.
    if (section === 0) add('kick', b0, 0.9);
    else if (section === 3) for (let q = 0; q < 4; q++) add('kick', b0 + q, 0.95);
    else {
      add('kick', b0, 0.95);
      add('kick', b0 + 2, 0.9);
      if (section === 2) add('kick', b0 + 2.5, 0.6);
    }
    // Snare (and clap in the lift).
    if (section > 0) {
      for (const q of [1, 3]) {
        add('snare', b0 + q, 0.75);
        if (section === 2) add('clap', b0 + q, 0.5);
      }
    }
    // Hats.
    const step = section === 3 ? 0.25 : 0.5;
    for (let t = 0; t < SAMPLE_BEATS_PER_BAR; t += step) {
      const offBeat = t % 1 !== 0;
      if (section === 2 && offBeat && t % 1 === 0.5) add('openHat', b0 + t, 0.32);
      else add('hat', b0 + t, offBeat ? 0.28 : 0.4);
    }
    // Fill at the end of the last bar.
    if (bar === bars - 1 && lastOfSection) {
      for (const [t, g] of [
        [2, 0.7],
        [2.5, 0.75],
        [3, 0.8],
        [3.25, 0.8],
        [3.5, 0.85],
        [3.75, 0.9],
      ] as const) {
        add('tom', b0 + t, g);
      }
    }
  }
  const end = bars * SAMPLE_BEATS_PER_BAR;
  add('kick', end, 1);
  add('crash', end, 0.6);
  return hits;
}

// ---------------------------------------------------------------------------------------------------------------
// Voices
// ---------------------------------------------------------------------------------------------------------------

/** Deterministic noise in -1..1 (mulberry32). */
function noise(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (((t ^ (t >>> 14)) >>> 0) / 4294967296) * 2 - 1;
  };
}

const VOICE_SEC: Readonly<Record<DrumVoice, number>> = {
  kick: 0.45,
  snare: 0.3,
  hat: 0.06,
  openHat: 0.32,
  clap: 0.25,
  tom: 0.35,
  crash: 1.4,
};

/** One-shot sample of a drum voice (mono, peak about 1). */
export function drumVoice(voice: DrumVoice, sampleRate = SAMPLE_RATE): Float32Array<ArrayBuffer> {
  const n = Math.round(VOICE_SEC[voice] * sampleRate);
  const out = new Float32Array(n);
  const rnd = noise(voice.length * 7919 + n);
  let phase = 0;
  let prev = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    let v = 0;
    switch (voice) {
      case 'kick': {
        const f = 45 + 105 * Math.exp(-t * 28);
        phase += (2 * Math.PI * f) / sampleRate;
        v = Math.sin(phase) * Math.exp(-t * 7.5) + (t < 0.004 ? rnd() * 0.4 * (1 - t / 0.004) : 0);
        break;
      }
      case 'snare': {
        phase += (2 * Math.PI * 185) / sampleRate;
        v = Math.sin(phase) * 0.45 * Math.exp(-t * 28) + rnd() * 0.7 * Math.exp(-t * 16);
        break;
      }
      case 'tom': {
        const f = 82 + 40 * Math.exp(-t * 14);
        phase += (2 * Math.PI * f) / sampleRate;
        v = Math.sin(phase) * Math.exp(-t * 9);
        break;
      }
      case 'clap': {
        const burst = [0, 0.011, 0.022].some((s0) => t >= s0 && t < s0 + 0.008) ? 1 : 0;
        v = rnd() * (burst * 0.8 + 0.6 * Math.exp(-Math.max(0, t - 0.022) * 22) * (t >= 0.022 ? 1 : 0));
        break;
      }
      case 'hat':
      case 'openHat':
      case 'crash': {
        // First-difference high-pass over white noise: a bright, metallic hiss.
        const x = rnd();
        const hp = x - prev;
        prev = x;
        const decay = voice === 'hat' ? 60 : voice === 'openHat' ? 9 : 2.6;
        v = hp * 0.5 * Math.exp(-t * decay);
        break;
      }
    }
    out[i] = v;
  }
  return out;
}

const VOICES: readonly DrumVoice[] = ['kick', 'snare', 'hat', 'openHat', 'clap', 'tom', 'crash'];
const HIGH_PASS_VOICES: ReadonlySet<DrumVoice> = new Set<DrumVoice>(['hat', 'openHat', 'crash']);
const MASTER_GAIN = 0.7;

export interface RenderedSong {
  readonly channels: readonly Float32Array[];
  readonly sampleRate: number;
  readonly durationSec: number;
}

/** Mixes the hits here (hosts without OfflineAudioContext); same hits, gains and master level. */
export function mixDrumLoop(
  hits: readonly DrumHit[],
  sampleRate: number,
  durationSec: number,
  bpm = SAMPLE_BPM,
): RenderedSong {
  const length = Math.ceil(durationSec * sampleRate);
  const mono = new Float32Array(length);
  const shots = new Map(VOICES.map((v) => [v, drumVoice(v, sampleRate)]));
  for (const h of hits) {
    const shot = shots.get(h.voice);
    if (shot === undefined) continue;
    const start = Math.round(((h.beat * 60) / bpm) * sampleRate);
    for (let i = 0; i < shot.length && start + i < length; i++) {
      mono[start + i] = (mono[start + i] ?? 0) + (shot[i] ?? 0) * h.gain * MASTER_GAIN;
    }
  }
  for (let i = 0; i < length; i++) mono[i] = Math.max(-1, Math.min(1, mono[i] ?? 0));
  return { channels: [mono, Float32Array.from(mono)], sampleRate, durationSec: length / sampleRate };
}

/** Creates the offline context (null when the host has none). */
export type OfflineContextFactory = (
  channels: number,
  length: number,
  sampleRate: number,
) => OfflineAudioContext | null;

const defaultFactory: OfflineContextFactory = (channels, length, sampleRate) =>
  typeof OfflineAudioContext === 'function' ? new OfflineAudioContext(channels, length, sampleRate) : null;

/** Renders the drum loop (OfflineAudioContext, or the mix above without one). */
export async function renderDrumLoop(
  opts: { readonly sampleRate?: number; readonly bars?: number; readonly createContext?: OfflineContextFactory } = {},
): Promise<RenderedSong> {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const bars = opts.bars ?? SAMPLE_BARS;
  const durationSec = sampleDurationSec(bars);
  const hits = drumPattern(bars);
  const length = Math.ceil(durationSec * sampleRate);
  const ctx = (opts.createContext ?? defaultFactory)(2, length, sampleRate);
  if (ctx === null) return mixDrumLoop(hits, sampleRate, durationSec);

  const master = ctx.createGain();
  master.gain.value = MASTER_GAIN;
  master.connect(ctx.destination);
  const highPass = ctx.createBiquadFilter();
  highPass.type = 'highpass';
  highPass.frequency.value = 6500;
  highPass.connect(master);
  const buffers = new Map<DrumVoice, AudioBuffer>();
  for (const v of VOICES) {
    const shot = drumVoice(v, sampleRate);
    const buffer = ctx.createBuffer(1, shot.length, sampleRate);
    buffer.copyToChannel(shot, 0);
    buffers.set(v, buffer);
  }
  for (const h of hits) {
    const buffer = buffers.get(h.voice);
    if (buffer === undefined) continue;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.value = h.gain;
    src.connect(gain);
    gain.connect(HIGH_PASS_VOICES.has(h.voice) ? highPass : master);
    src.start((h.beat * 60) / SAMPLE_BPM);
  }
  const rendered = await ctx.startRendering();
  const channels: Float32Array[] = [];
  for (let c = 0; c < rendered.numberOfChannels; c++) {
    const data = rendered.getChannelData(c);
    const copy = new Float32Array(data.length);
    copy.set(data);
    channels.push(copy);
  }
  return { channels, sampleRate: rendered.sampleRate, durationSec: rendered.length / rendered.sampleRate };
}

/** The sample song as a 16-bit WAV file. */
export async function sampleSongWav(opts: { readonly createContext?: OfflineContextFactory } = {}): Promise<{
  readonly bytes: Uint8Array;
  readonly durationSec: number;
  readonly sampleRate: number;
  readonly channels: number;
}> {
  const song = await renderDrumLoop(opts);
  return {
    bytes: encodeWav(song.channels, song.sampleRate, 16),
    durationSec: song.durationSec,
    sampleRate: song.sampleRate,
    channels: song.channels.length,
  };
}
